import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Search, ChevronDown, ChevronUp, ChevronsUpDown, X, Tag, Sparkles, Building2, Calendar,
  SlidersHorizontal, ChevronLeft, ChevronRight, Phone, Mail, Users, Loader2,
  Table as TableIcon, CalendarDays, ListFilter, Check, Plus, Trash2, TrendingUp, TrendingDown, Minus, Repeat2,
} from 'lucide-react'

const NEW_LEAD = Object.freeze({ __new: true })
import { api } from '../lib/api.js'
import { smoothPath } from '../lib/chart.js'
import { FollowUpDrawer } from './FollowUps.jsx'
import { CHANNEL_DOT, FollowUpItemList, CompanyCombobox, ConfirmDialog, CreatedByChip } from '../components/ui.jsx'
import { CompanyDrawer } from './Companies.jsx'
import { validatePhone, validateEmail, firstError } from '../lib/validate.js'

const STAGES = [
  'รอติดต่อ', 'รอติดต่อกลับ', 'คุยกับ HR แล้ว',
  'Follow up', 'Done', 'Cancel', 'ไม่รับสาย',
]
const SOURCES = [
  'Cold Call', 'ADMIN', 'Webform', 'Facebook', 'LINE', 'Event', 'Walkin', 'อื่นๆ',
]
const STAGE_DOT = {
  'รอติดต่อ':                'bg-zinc-400',
  'รอติดต่อกลับ':    'bg-amber-500',
  'คุยกับ HR แล้ว':            'bg-blue-500',
  'Follow up':              'bg-violet-500',
  'Done':                   'bg-emerald-500',
  'Cancel':                 'bg-rose-500',
  'ไม่รับสาย':                'bg-orange-500',
}
const STAGE_BADGE = {
  'รอติดต่อ':                'bg-zinc-50 ring-zinc-200/70 text-zinc-700',
  'รอติดต่อกลับ':    'bg-amber-50 ring-amber-200/80 text-amber-900',
  'คุยกับ HR แล้ว':            'bg-blue-50 ring-blue-200/80 text-blue-800',
  'Follow up':              'bg-violet-50 ring-violet-200/80 text-violet-800',
  'Done':                   'bg-emerald-50 ring-emerald-200/80 text-emerald-800',
  'Cancel':                 'bg-rose-50 ring-rose-200/80 text-rose-800',
  'ไม่รับสาย':                'bg-orange-50 ring-orange-200/80 text-orange-800',
}
const SOURCE_DOT = {
  'Cold Call': 'bg-blue-500',
  'ADMIN':     'bg-slate-500',
  'Webform':   'bg-violet-500',
  'Facebook':  'bg-sky-600',
  'LINE':      'bg-emerald-500',
  'Event':     'bg-pink-500',
  'Walkin':    'bg-teal-500',
  'อื่นๆ':       'bg-zinc-400',
}
const SOURCE_BADGE = {
  'Cold Call': 'bg-blue-50 ring-blue-200/70 text-blue-800',
  'ADMIN':     'bg-slate-50 ring-slate-200/70 text-slate-700',
  'Webform':   'bg-violet-50 ring-violet-200/70 text-violet-800',
  'Facebook':  'bg-sky-50 ring-sky-200/70 text-sky-800',
  'LINE':      'bg-emerald-50 ring-emerald-200/70 text-emerald-800',
  'Event':     'bg-pink-50 ring-pink-200/70 text-pink-800',
  'Walkin':    'bg-teal-50 ring-teal-200/70 text-teal-800',
  'อื่นๆ':       'bg-zinc-50 ring-zinc-200/70 text-zinc-600',
}
const PAGE_SIZES = [25, 50, 100, 200]

const VIEWS = [
  { v: 'table', label: 'Table',    icon: TableIcon    },
  { v: 'date',  label: 'By Date',  icon: CalendarDays },
]

// Base card. The "with overflow-hidden" variant is for content cards that
// must clip their inner table/borders to the rounded corners; the toolbar
// uses the plain variant so its popovers can escape its bounds.
const CARD_BASE = 'bg-white rounded-2xl ring-1 ring-zinc-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
const CARD      = `${CARD_BASE} overflow-hidden`

export default function Leads() {
  const [q, setQ]               = useState('')
  const [stage, setStage]       = useState([])
  const [source, setSource]     = useState([])
  const [hasCompany, setHasCompany] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo]     = useState('')
  const [page, setPage]         = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [sort, setSort]         = useState('created')
  const [dir, setDir]           = useState('desc')
  const [viewMode, setViewMode] = useState('table')   // 'table' | 'date' | 'stage'

  const [data, setData]         = useState(null)
  const [error, setError]       = useState(null)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())
  const [editing, setEditing]   = useState(null)
  const [lastViewedId, setLastViewedId] = useState(() => {
    const v = parseInt(sessionStorage.getItem('lastViewedLeadId') || '', 10)
    return Number.isFinite(v) ? v : null
  })
  const [toast, setToast]       = useState(null)
  const [fuEditing, setFuEditing] = useState(null)
  const [lockedLead, setLockedLead] = useState(null)
  const [stats, setStats] = useState(null)
  const [statsPeriod, setStatsPeriod] = useState(7)   // 7 | 14 | 30

  const refreshStats = () => { api.leads.stats(statsPeriod).then(setStats).catch(() => {}) }
  useEffect(refreshStats, [statsPeriod])

  // The search box drives a LIKE '%q%' over the whole lead table, which no
  // index can help with, plus a matching COUNT(*) — about 8,300 rows read per
  // request. Bound straight to onChange that was a round trip PER KEYSTROKE.
  // The input still updates instantly; only the query waits for a pause.
  const [qDebounced, setQDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300)
    return () => clearTimeout(t)
  }, [q])

  // Open a lead: show the row data immediately, then enrich with the full
  // detail (includes follow_ups) from the server in the background.
  const openLead = (row) => {
    if (!row || row.__new) { setEditing(row); return }
    sessionStorage.setItem('lastViewedLeadId', String(row.id))
    setLastViewedId(row.id)
    setEditing(row)
    api.leads.get(row.id)
      .then((full) => setEditing((cur) => (cur && cur.id === row.id ? full : cur)))
      .catch(() => {})
  }
  const reopenLead = (id) => {
    setEditing(null)
    setTimeout(() => api.leads.get(id).then(setEditing).catch(() => {}), 50)
  }

  useEffect(() => { setPage(1); setCollapsedGroups(new Set()) },
    [q, stage, source, hasCompany, dateFrom, dateTo, pageSize, sort, dir, viewMode])

  // The list reloads whenever a filter moves. STATS DOES NOT, and used to.
  // It answers "how many leads this period, and how many per month" — a
  // question that no filter, page, sort or view mode changes the answer to.
  // Riding along here it cost ~5,900 rows read every time someone clicked a
  // chip or turned a page, and it ran more often than the list it followed.
  // It reloads on the only two things that move it: the period selector, and
  // a lead being created or deleted (refresh(), below).
  const refreshList = () => {
    setError(null)
    api.leads.list({
      q: qDebounced, stage, source, has_company: hasCompany,
      date_from: dateFrom, date_to: dateTo,
      sort, dir, page, page_size: pageSize, view: viewMode,
    })
      .then(setData)
      .catch((e) => setError(e.message))
  }
  useEffect(refreshList,
    [qDebounced, stage, source, hasCompany, dateFrom, dateTo, sort, dir, page, pageSize, viewMode])

  // Save and delete move both halves.
  const refresh = () => { refreshList(); refreshStats() }

  const handleSort = (field) => {
    if (sort === field) {
      setDir(dir === 'asc' ? 'desc' : 'asc')
    } else {
      setSort(field)
      setDir(field === 'created' ? 'desc' : 'asc')
    }
  }

  const activeCount =
    (stage.length ? 1 : 0) + (source.length ? 1 : 0) + (hasCompany ? 1 : 0) +
    (dateFrom || dateTo ? 1 : 0)

  const clearAll = () => {
    setQ(''); setStage([]); setSource([]); setHasCompany(''); setDateFrom(''); setDateTo('')
  }

  const total       = data?.total ?? 0
  const totalPages  = Math.max(1, Math.ceil(total / pageSize))
  const fromIdx     = total === 0 ? 0 : (page - 1) * pageSize + 1
  const toIdx       = Math.min(total, page * pageSize)

  // Group rows by the active view mode
  const groups = useMemo(() => {
    if (!data) return []
    if (viewMode === 'date') return groupRowsByDate(data.rows)
    return []
  }, [data, viewMode])

  // Column visibility per view
  const showCreated = viewMode !== 'date'

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1400px] mx-auto">

      {/* Page header */}
      <header className="mb-5 md:mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
        <div className="flex items-start gap-3">
          {/* Brand accent bar */}
          <span aria-hidden className="hidden md:block mt-1.5 w-1 h-9 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
          <div>
            <h1 className="text-[24px] md:text-[28px] font-bold tracking-tight text-zinc-900 leading-tight">Lead</h1>
            <p className="text-[13px] md:text-[13.5px] text-zinc-500 mt-1">Inquiries from Facebook, web form, cold call, walk-in.</p>
          </div>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2.5">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(NEW_LEAD)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-500/25 transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} />
              New lead
            </button>
            <ViewSwitcher value={viewMode} onChange={setViewMode} />
          </div>
          <div className="text-[12.5px] text-zinc-500 tabular-nums inline-flex items-baseline gap-1.5">
            {data ? (
              <>
                <span className="text-[15px] font-semibold text-zinc-900">{total.toLocaleString()}</span>
                <span>leads</span>
                <span className="text-zinc-300 mx-1">·</span>
                <span>showing</span>
                <span className="font-medium text-zinc-700">{fromIdx.toLocaleString()}–{toIdx.toLocaleString()}</span>
              </>
            ) : 'Loading…'}
          </div>
        </div>
      </header>

      {/* Summary — recent-period hero + 12-month line chart (Event-source excluded) */}
      <LeadStats stats={stats} period={statsPeriod} onPeriodChange={setStatsPeriod} />

      {/* Toolbar — uses CARD_BASE (no overflow-hidden) so popovers can escape */}
      <div className={`${CARD_BASE} relative z-10`}>
        <div className="px-3 py-2.5 flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
          <div className="relative w-full md:flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหา (ชื่อ · เบอร์ · บริษัท)"
              className="w-full pl-9 pr-3 py-2 bg-transparent text-[13.5px] placeholder:text-zinc-400 focus:outline-none"
            />
          </div>

          <div className="hidden md:block h-6 w-px bg-zinc-200" />

          <div className="flex items-center gap-2 flex-wrap">
            <FilterButton icon={Tag} label="Stage" count={stage.length}>
              <ChipPanel options={STAGES} value={stage} onChange={setStage} dotMap={STAGE_DOT} />
            </FilterButton>
            <FilterButton icon={Sparkles} label="Source" count={source.length}>
              <ChipPanel options={SOURCES} value={source} onChange={setSource} dotMap={SOURCE_DOT} />
            </FilterButton>
            <FilterButton icon={Building2} label="Company" count={hasCompany ? 1 : 0}>
              <SegmentedPanel
                options={[
                  { v: '',    label: 'All' },
                  { v: 'yes', label: 'Linked' },
                  { v: 'no',  label: 'Not yet linked' },
                ]}
                value={hasCompany} onChange={setHasCompany}
              />
            </FilterButton>
            <FilterButton icon={Calendar} label="Created" count={dateFrom || dateTo ? 1 : 0}>
              <DatePanel from={dateFrom} to={dateTo}
                onChange={(f, t) => { setDateFrom(f); setDateTo(t) }} />
            </FilterButton>

            {activeCount > 0 && (
              <button onClick={clearAll}
                className="px-2.5 py-1.5 rounded-md text-[12px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50">
                Clear
              </button>
            )}
          </div>
        </div>

        {activeCount > 0 && (
          <div className="px-3 pb-3 -mt-1 flex flex-wrap items-center gap-1.5">
            {stage.map((s) => (
              <ActiveChip key={`st-${s}`} icon={Tag} label="Stage" value={s}
                onRemove={() => setStage(stage.filter((x) => x !== s))} />
            ))}
            {source.map((s) => (
              <ActiveChip key={`sr-${s}`} icon={Sparkles} label="Source" value={s}
                onRemove={() => setSource(source.filter((x) => x !== s))} />
            ))}
            {hasCompany && (
              <ActiveChip icon={Building2} label="Company"
                value={hasCompany === 'yes' ? 'Linked' : 'Not yet linked'}
                onRemove={() => setHasCompany('')} />
            )}
            {(dateFrom || dateTo) && (
              <ActiveChip icon={Calendar} label="Created"
                value={`${dateFrom || '…'} → ${dateTo || '…'}`}
                onRemove={() => { setDateFrom(''); setDateTo('') }} />
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px]">{error}</div>
      )}

      {/* List area */}
      <div className="mt-4">
        {viewMode === 'table'
          ? <TableView data={data} sort={sort} dir={dir} handleSort={handleSort}
                       showCreated activeCount={activeCount} clearAll={clearAll}
                       onRowClick={openLead} lastViewedId={lastViewedId} />
          : <GroupedView data={data} groups={groups} viewMode={viewMode}
                         sort={sort} dir={dir} handleSort={handleSort}
                         showCreated={showCreated}
                         activeCount={activeCount} clearAll={clearAll}
                         collapsedGroups={collapsedGroups} setCollapsedGroups={setCollapsedGroups}
                         onRowClick={openLead} lastViewedId={lastViewedId} />}
      </div>

      {/* Pagination */}
      {data && total > 0 && (
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12.5px] text-zinc-600">
          <div className="tabular-nums text-center md:text-left order-2 md:order-1">
            {fromIdx.toLocaleString()}–{toIdx.toLocaleString()} of {total.toLocaleString()}
          </div>
          <div className="order-1 md:order-2 flex justify-center">
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          </div>
          <div className="hidden md:flex items-center gap-2 order-3">
            <span className="text-zinc-500">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              className="px-2 py-1 bg-white border border-zinc-200 rounded-md text-[12px] font-medium hover:border-zinc-300 cursor-pointer"
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      <LeadDrawer
        lead={editing}
        onClose={() => setEditing(null)}
        onSaved={(isNew) => {
          setEditing(null)
          refresh()
          setToast({ id: Date.now(), message: isNew ? 'Lead created' : 'Changes saved' })
        }}
        onDeleted={() => {
          setEditing(null)
          refresh()
          setToast({ id: Date.now(), message: 'Lead deleted' })
        }}
        onAddFollowUp={(lead) => {
          setLockedLead({ id: lead.id, name: lead.name })
          // Seed company from the lead when present — drawer pre-selects it.
          setFuEditing({
            __new: true,
            lead_id:      lead.id,
            lead_name:    lead.name,
            company_id:   lead.company_id   ?? null,
            company_name: lead.company_name || null,
          })
        }}
        onEditFollowUp={(fu, lead) => { setLockedLead(lead); setFuEditing(fu) }}
      />

      <FollowUpDrawer
        followUp={fuEditing}
        lockedLead={lockedLead}
        onClose={() => { setFuEditing(null); setLockedLead(null) }}
        onSaved={(isNew) => {
          setFuEditing(null); setLockedLead(null)
          setToast({ id: Date.now(), message: isNew ? 'Follow-up logged' : 'Follow-up updated' })
          if (editing && !editing.__new) reopenLead(editing.id)
        }}
        onDeleted={() => {
          setFuEditing(null); setLockedLead(null)
          setToast({ id: Date.now(), message: 'Follow-up deleted' })
          if (editing && !editing.__new) reopenLead(editing.id)
        }}
        onAddAnother={(company) => {
          setFuEditing({ __new: true, company_id: company.id, company_name: company.name })
        }}
      />

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  )
}

/* ──────────────────────────────── toast ──────────────────────────────── */

function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(onDone, 2200)
    return () => clearTimeout(t)
  }, [toast, onDone])

  if (!toast) return null

  return (
    <div
      key={toast.id}
      role="status"
      aria-live="polite"
      className="fixed top-[60px] md:top-5 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-tt-toast-in"
    >
      <div className="bg-zinc-900 text-white text-[13px] font-medium pl-2.5 pr-3.5 py-2 rounded-full shadow-[0_8px_28px_-6px_rgba(0,0,0,0.35)] flex items-center gap-2 ring-1 ring-zinc-700/40">
        <span className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shrink-0 shadow-sm shadow-amber-500/30">
          <Check size={12} strokeWidth={3} className="text-white" />
        </span>
        {toast.message}
      </div>
    </div>
  )
}

/* ──────────────────────────────── summary (hero + monthly chart) ──────────────────────────────── */

const PERIODS = [
  { v: 7,  label: '7d'  },
  { v: 14, label: '14d' },
  { v: 30, label: '1mo' },
]
const PERIOD_LONG = { 7: 'Last 7 days', 14: 'Last 14 days', 30: 'Last 1 month' }

function LeadStats({ stats, period, onPeriodChange }) {
  return (
    <section className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <RecentHero stats={stats?.hero} period={period} onPeriodChange={onPeriodChange} />
      <MonthlyTrend monthly={stats?.monthly} />
    </section>
  )
}

function RecentHero({ stats, period, onPeriodChange }) {
  const loading = !stats
  const count = stats?.count ?? 0
  const prev  = stats?.prev  ?? 0
  const delta = stats?.delta_pct

  let trendIcon, trendLabel, trendCls
  if (delta == null) {
    trendIcon = TrendingUp
    trendLabel = count === 0 ? 'no prior data' : `+${count} new`
    trendCls = count > 0 ? 'text-emerald-700 bg-emerald-50 ring-emerald-200/70' : 'text-zinc-500 bg-zinc-50 ring-zinc-200/70'
  } else if (delta > 0) {
    trendIcon = TrendingUp;   trendLabel = `+${delta}%`;  trendCls = 'text-emerald-700 bg-emerald-50 ring-emerald-200/70'
  } else if (delta < 0) {
    trendIcon = TrendingDown; trendLabel = `${delta}%`;   trendCls = 'text-rose-700 bg-rose-50 ring-rose-200/70'
  } else {
    trendIcon = Minus;        trendLabel = 'no change';   trendCls = 'text-zinc-600 bg-zinc-50 ring-zinc-200/70'
  }
  const TrendIcon = trendIcon

  return (
    <div className={`${CARD} p-5`}>
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">{PERIOD_LONG[period]}</div>
          <div className="inline-flex bg-zinc-100/80 rounded-md p-0.5 ring-1 ring-zinc-200/60">
            {PERIODS.map((p) => {
              const on = p.v === period
              return (
                <button
                  key={p.v}
                  onClick={() => onPeriodChange(p.v)}
                  className={'px-2 py-0.5 rounded text-[10.5px] font-medium transition-colors ' +
                    (on ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-900')}
                >
                  {p.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-2 flex items-baseline gap-2">
          <div className="text-[34px] font-bold tabular-nums text-zinc-900 leading-none">
            {loading ? '–' : count.toLocaleString()}
          </div>
          <div className="text-[12px] text-zinc-500">new leads</div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${trendCls}`}>
            <TrendIcon size={12} strokeWidth={2.5} />
            {trendLabel}
          </span>
          <span className="text-[11.5px] text-zinc-400 tabular-nums">vs prior {period}d ({loading ? '–' : prev.toLocaleString()})</span>
        </div>
        <p className="mt-3 text-[11px] text-zinc-400">Event-sourced leads not counted.</p>
      </div>
    </div>
  )
}

function MonthlyTrend({ monthly }) {
  const loading = !monthly
  const data = monthly || []
  const total = data.reduce((acc, m) => acc + m.count, 0)
  const [hoverIdx, setHoverIdx] = useState(null)

  // SVG path geometry (stretched horizontally — only the line + area get stretched;
  // dots / labels / tooltip are HTML so they stay round / unstretched).
  const W = 540, H = 100
  const padT = 6, padB = 6
  const inner = { x: 0, y: padT, w: W, h: H - padT - padB }
  const max = Math.max(1, ...data.map((m) => m.count))
  const stepX = data.length > 1 ? inner.w / (data.length - 1) : 0
  const pts = data.map((m, i) => {
    const x = i * stepX
    const y = inner.y + inner.h - (m.count / max) * inner.h
    return { x, y, normX: data.length > 1 ? i / (data.length - 1) : 0.5, normY: m.count / max, m }
  })
  const linePath = smoothPath(pts)
  const areaPath = pts.length
    ? `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${inner.y + inner.h} L 0 ${inner.y + inner.h} Z`
    : ''

  // Sparse labels: first / middle / last
  const labelIdx = data.length <= 1 ? [0] : [0, Math.floor(data.length / 2), data.length - 1]
  const fmtMonth = (s) => {
    if (!s) return ''
    const [y, m] = s.split('-').map(Number)
    const d = new Date(y, m - 1, 1)
    return d.toLocaleDateString('en-US', { month: 'short' }) + ' ' + String(y).slice(2)
  }

  const handleMove = (e) => {
    if (!pts.length) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const ratio = Math.max(0, Math.min(1, x / rect.width))
    const idx = Math.round(ratio * (pts.length - 1))
    setHoverIdx(idx)
  }

  return (
    <div className={`${CARD} p-5 md:col-span-2 relative`}>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">New leads per month</div>
          <p className="text-[12px] text-zinc-400 mt-0.5">Last 12 months · Event-source excluded</p>
        </div>
        <div className="text-[12px] text-zinc-500 tabular-nums">
          {loading ? '–' : <><span className="text-zinc-900 font-semibold text-[14px]">{total.toLocaleString()}</span> total</>}
        </div>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="h-[120px] flex items-center justify-center text-zinc-300 text-[12px]">Loading…</div>
        ) : (
          <div className="relative h-[120px] cursor-crosshair"
               onMouseMove={handleMove}
               onMouseLeave={() => setHoverIdx(null)}>
            {/* SVG handles only the stretched paths (line + area + guides) */}
            <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="ttFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#f59e0b" stopOpacity="0.30" />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {[0.25, 0.5, 0.75, 1].map((t) => (
                <line key={t} x1={0} x2={W}
                  y1={inner.y + inner.h - inner.h * t}
                  y2={inner.y + inner.h - inner.h * t}
                  stroke="#f4f4f5" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              ))}
              {pts.length > 0 && <path d={areaPath} fill="url(#ttFill)" />}
              {pts.length > 0 && (
                <path d={linePath} fill="none" stroke="#f59e0b" strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              )}
            </svg>

            {/* Hover vertical guide */}
            {hoverIdx != null && pts[hoverIdx] && (
              <div aria-hidden
                className="absolute top-0 bottom-0 w-px bg-zinc-300/80 pointer-events-none"
                style={{ left: `${pts[hoverIdx].normX * 100}%` }} />
            )}

            {/* Data dots — HTML so they stay perfectly round */}
            {pts.map((p, i) => {
              const active = i === hoverIdx
              return (
                <div
                  key={i}
                  className={'absolute rounded-full bg-white border-2 border-amber-500 -translate-x-1/2 -translate-y-1/2 transition-transform ' +
                    (active ? 'w-3 h-3 shadow-sm shadow-amber-500/30' : 'w-2 h-2')}
                  style={{
                    left: `${p.normX * 100}%`,
                    top:  `${(p.y / H) * 100}%`,
                  }}
                />
              )
            })}

            {/* Tooltip pinned above the hovered dot */}
            {hoverIdx != null && pts[hoverIdx] && (
              <div
                className="absolute -translate-x-1/2 -translate-y-full -mt-3 pointer-events-none"
                style={{ left: `${pts[hoverIdx].normX * 100}%`, top: `${(pts[hoverIdx].y / H) * 100}%` }}
              >
                <div className="bg-zinc-900 text-white text-[11px] font-medium px-2 py-1 rounded-md shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)] whitespace-nowrap tabular-nums">
                  <span className="text-zinc-400 mr-1.5">{fmtMonth(pts[hoverIdx].m.month)}</span>
                  <span>{pts[hoverIdx].m.count.toLocaleString()} leads</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* X-axis labels — HTML so they stay at normal size */}
        {!loading && data.length > 0 && (
          <div className="relative mt-1 h-4 text-[10px] text-zinc-400">
            {labelIdx.map((i) => (
              <span key={i}
                className="absolute -translate-x-1/2 whitespace-nowrap"
                style={{
                  left: `${(i / Math.max(1, data.length - 1)) * 100}%`,
                  transform: i === 0 ? 'translateX(0)' : i === data.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                }}>
                {fmtMonth(data[i]?.month)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ──────────────────────────────── view switcher ──────────────────────────────── */

function ViewSwitcher({ value, onChange }) {
  return (
    <div className="inline-flex bg-zinc-100/80 ring-1 ring-zinc-200/60 rounded-lg p-0.5">
      {VIEWS.map(({ v, label, icon: Icon }) => {
        const on = value === v
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] font-medium transition-colors ' +
              (on ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-900')
            }
          >
            <Icon size={13} strokeWidth={2.25} className={on ? 'text-amber-600' : 'text-zinc-400'} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

/* ──────────────────────────────── flat table view ──────────────────────────────── */

function TableView({ data, sort, dir, handleSort, showCreated, activeCount, clearAll, onRowClick, lastViewedId }) {
  const cols = 4 + (showCreated ? 1 : 0)
  return (
    <div className={CARD}>
      <table className="w-full table-fixed text-[13px]">
        <ColGroup showCreated={showCreated} />
        <ColHeaders sort={sort} dir={dir} onSort={handleSort} showCreated={showCreated} />
        <tbody>
          {!data && <Loading cols={cols} />}
          {data && data.rows.length === 0 && <EmptyState cols={cols} activeCount={activeCount} clearAll={clearAll} />}
          {data && data.rows.map((l) => (
            <LeadRow key={l.id} lead={l} showCreated={showCreated} onClick={() => onRowClick(l)} isLastViewed={l.id === lastViewedId} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ──────────────────────────────── grouped view (card per group) ──────────────────────────────── */

function GroupedView({ data, groups, viewMode, sort, dir, handleSort, showCreated,
                      activeCount, clearAll, collapsedGroups, setCollapsedGroups, onRowClick, lastViewedId }) {
  const cols = 4 + (showCreated ? 1 : 0)

  const toggle = (key) => {
    const next = new Set(collapsedGroups)
    if (next.has(key)) next.delete(key); else next.add(key)
    setCollapsedGroups(next)
  }

  return (
    <div className="space-y-4">
      {/* Shared column-header card */}
      <div className={CARD}>
        <table className="w-full table-fixed text-[13px]">
          <ColGroup showCreated={showCreated} />
          <ColHeaders sort={sort} dir={dir} onSort={handleSort} showCreated={showCreated} />
          <tbody>
            <tr><td colSpan={cols} className="h-0 p-0 border-0" /></tr>
          </tbody>
        </table>
      </div>

      {/* Group cards */}
      {!data && (
        <div className={CARD}>
          <table className="w-full"><tbody><Loading cols={cols} /></tbody></table>
        </div>
      )}
      {data && groups.length === 0 && (
        <div className={CARD}>
          <table className="w-full"><tbody><EmptyState cols={cols} activeCount={activeCount} clearAll={clearAll} /></tbody></table>
        </div>
      )}
      {data && groups.map((g) => {
        const collapsed = collapsedGroups.has(g.key)
        return (
          <div key={g.key} className={CARD}>
            <button
              onClick={() => toggle(g.key)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
            >
              <ChevronDown
                size={14} strokeWidth={2.5}
                className={'text-zinc-500 transition-transform ' + (collapsed ? '-rotate-90' : '')}
              />
              <span className="text-[13px] font-semibold text-zinc-800 tabular-nums">{g.label}</span>
              <span className="text-[11.5px] text-zinc-500 tabular-nums ml-1">
                · {g.rows.length} {g.rows.length === 1 ? 'record' : 'records'}
              </span>
            </button>
            {!collapsed && (
              <table className="w-full table-fixed text-[13px]">
                <ColGroup showCreated={showCreated} />
                <tbody>
                  {g.rows.map((l) => (
                    <LeadRow key={l.id} lead={l} showCreated={showCreated} onClick={() => onRowClick(l)} isLastViewed={l.id === lastViewedId} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ──────────────────────────────── column primitives ──────────────────────────────── */

function ColGroup({ showCreated }) {
  return (
    <colgroup>
      <col />
      <col className="hidden md:table-column md:w-[110px]" />
      <col className="hidden md:table-column md:w-[170px]" />
      <col className="hidden md:table-column md:w-[200px]" />
      {showCreated && <col className="hidden md:table-column md:w-[110px]" />}
    </colgroup>
  )
}

function ColHeaders({ sort, dir, onSort, showCreated }) {
  return (
    <thead>
      <tr className="border-b border-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
        <SortHeader field="name"   label="Name"   sort={sort} dir={dir} onClick={onSort} />
        <th className="hidden md:table-cell px-5 py-3 text-right whitespace-nowrap">Employees</th>
        <SortHeader field="source" label="Source" sort={sort} dir={dir} onClick={onSort} className="hidden md:table-cell" />
        <SortHeader field="stage"  label="Stage"  sort={sort} dir={dir} onClick={onSort} className="hidden md:table-cell" />
        {showCreated && <SortHeader field="created" label="Created" sort={sort} dir={dir} onClick={onSort} align="right" className="hidden md:table-cell" />}
      </tr>
    </thead>
  )
}

function Loading({ cols }) {
  return <tr><td colSpan={cols} className="px-5 py-12 text-center text-zinc-400">Loading…</td></tr>
}

function EmptyState({ cols, activeCount, clearAll }) {
  return (
    <tr><td colSpan={cols} className="px-5 py-12 text-center">
      <SlidersHorizontal size={20} className="mx-auto text-zinc-300" />
      <div className="mt-2 text-[13px] text-zinc-500">No leads match these filters.</div>
      {activeCount > 0 && (
        <button onClick={clearAll}
          className="mt-2 text-[12.5px] text-amber-600 hover:text-amber-700 font-medium">
          Clear filters
        </button>
      )}
    </td></tr>
  )
}

/* ──────────────────────────────── lead row ──────────────────────────────── */

function LeadRow({ lead, showCreated, onClick, isLastViewed }) {
  const company = lead.company_name || lead.company_name_raw
  return (
    <tr
      onClick={onClick}
      className={
        'border-b border-zinc-100 last:border-b-0 transition-colors cursor-pointer ' +
        (isLastViewed
          ? 'bg-amber-50 hover:bg-amber-100/70 ring-1 ring-inset ring-amber-300/40'
          : 'hover:bg-amber-50/30')
      }
    >
      <td className="px-3 md:px-5 py-2.5">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-medium text-zinc-900 truncate" title={lead.name}>{lead.name}</span>
          {company && (
            <>
              <span className="text-zinc-300 shrink-0">·</span>
              <span className="text-[12.5px] text-zinc-500 truncate" title={company}>{company}</span>
            </>
          )}
        </div>
        <div className="md:hidden mt-1 flex items-center gap-1.5 flex-wrap">
          <StageBadge stage={lead.stage} />
          {lead.source && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 text-[10.5px] font-medium ${SOURCE_BADGE[lead.source] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${SOURCE_DOT[lead.source] || 'bg-zinc-400'}`} />
              {lead.source}
            </span>
          )}
          {lead.follow_up_count > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600 text-[10.5px] font-medium tabular-nums ring-1 ring-zinc-200/70">
              <Repeat2 size={9} strokeWidth={2.5} />
              {lead.follow_up_count}
            </span>
          )}
        </div>
      </td>
      <td className="hidden md:table-cell px-5 py-2.5 text-right text-zinc-700 tabular-nums align-top">
        {lead.employee_count_est?.toLocaleString() || <span className="text-zinc-300">—</span>}
      </td>
      <td className="hidden md:table-cell px-5 py-2.5 align-top">
        {lead.source
          ? (
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${SOURCE_BADGE[lead.source] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${SOURCE_DOT[lead.source] || 'bg-zinc-400'}`} />
              {lead.source}
            </span>
          )
          : <span className="text-zinc-300">—</span>}
      </td>
      <td className="hidden md:table-cell px-5 py-2.5 align-top">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StageBadge stage={lead.stage} />
          {lead.follow_up_count > 0 && (
            <span
              title={`${lead.follow_up_count} follow-up${lead.follow_up_count > 1 ? 's' : ''} logged`}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600 text-[10.5px] font-medium tabular-nums ring-1 ring-zinc-200/70"
            >
              <Repeat2 size={9} strokeWidth={2.5} />
              {lead.follow_up_count}
            </span>
          )}
        </div>
      </td>
      {showCreated && (
        <td className="hidden md:table-cell px-5 py-2.5 text-right text-zinc-400 text-[12px] tabular-nums align-top">
          {lead.created_at ? new Date(lead.created_at * 1000).toLocaleDateString('th-TH-u-ca-gregory') : '—'}
        </td>
      )}
    </tr>
  )
}

/* ──────────────────────────────── edit drawer ──────────────────────────────── */

function LeadDrawer({ lead, onClose, onSaved, onDeleted, onAddFollowUp, onEditFollowUp }) {
  const open  = !!lead
  const isNew = lead?.__new === true
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [err, setErr] = useState(null)

  // Lead → Company flow: opens the full CompanyDrawer with primary-contact
  // info pre-seeded from this lead. Replaces the old single-input mini form.
  const [newCoOpen, setNewCoOpen] = useState(false)

  useEffect(() => {
    if (lead) {
      setForm({
        name:               lead.name || '',
        phone:              lead.phone || '',
        email:              lead.email || '',
        employee_count_est: lead.employee_count_est ?? '',
        stage:              lead.stage || 'รอติดต่อ',
        source:             lead.source || '',
        remark:             lead.remark || '',
        company_name_raw:   lead.company_name_raw || '',
        company:            lead.company_id ? { id: lead.company_id, name: lead.company_name || '(unknown)' } : null,
      })
      setErr(null)
      setConfirmOpen(false)
      setNewCoOpen(false)
    }
  }, [lead?.id, lead?.__new])

  useEffect(() => {
    if (!open) return
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  const save = async () => {
    setErr(null)
    if (!form.name.trim()) { setErr('Name is required.'); return }
    // Phone is intentionally free-form text — no validation.
    const validationErr = firstError(
      validateEmail(form.email, 'Email'),
    )
    if (validationErr) { setErr(validationErr); return }
    const email = (form.email || '').trim()

    const payload = {
      name: form.name.trim(),
      phone: form.phone || null,
      email: email || null,
      employee_count_est: form.employee_count_est === '' ? null : parseInt(form.employee_count_est, 10),
      stage: form.stage,
      source: form.source || null,
      remark: form.remark.trim() || null,
      company_name_raw: form.company_name_raw?.trim() || null,
      company_id: form.company?.id ?? null,
    }

    setSaving(true)
    try {
      if (isNew) {
        await api.leads.create(payload)
      } else {
        await api.leads.update(lead.id, payload)
      }
      onSaved(isNew)
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  // Seed for the nested CompanyDrawer: company name defaults to whatever raw
  // text was on the lead, and pc_* are pulled from the lead's contact info so
  // staff don't have to retype.
  const companySeed = form ? {
    __new: true,
    name:    form.company_name_raw || '',
    pc_name:  form.name?.trim()    || '',
    pc_phone: form.phone           || '',
    pc_email: form.email?.trim()   || '',
  } : null

  // After the nested CompanyDrawer creates a company, link it to this lead and
  // clear the raw company-name field.
  const onCompanyCreated = (created) => {
    if (created?.id) {
      setForm((f) => ({ ...f, company: { id: created.id, name: created.name }, company_name_raw: '' }))
    }
    setNewCoOpen(false)
  }

  const doDelete = async () => {
    setErr(null); setDeleting(true)
    try {
      await api.leads.remove(lead.id)
      onDeleted?.(lead.id)
    } catch (e) { setErr(e.message) }
    finally { setDeleting(false); setConfirmOpen(false) }
  }

  if (!open || !form) {
    return <div className="fixed inset-0 pointer-events-none opacity-0" />
  }

  return (
    <div className="fixed inset-0 z-30">
      <div onClick={onClose} className="absolute inset-0 bg-zinc-900/20 backdrop-blur-[1px]" />
      <aside className="absolute right-0 top-0 h-full w-[460px] max-w-[100vw] bg-white shadow-2xl ring-1 ring-zinc-200 flex flex-col">
        <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-medium text-amber-600">
              {isNew ? 'New lead' : 'Edit lead'}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-[15px] font-semibold text-zinc-900 truncate" title={lead.name || ''}>
                {isNew ? (form.name || <span className="text-zinc-400 font-normal italic">Untitled</span>) : lead.name}
              </h2>
              {!isNew && lead && (
                <CreatedByChip
                  createdAtUnix={lead.created_at}
                  createdById={lead.created_by_xano_id}
                  fallbackCode={lead.created_by_code}
                />
              )}
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 flex items-center justify-center"
            aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          <Field label="Name *">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Phone" icon={Phone}>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="โทร"
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] tabular-nums focus:border-amber-500 focus:outline-none placeholder:text-zinc-300"
              />
            </Field>
            <Field label="Email" icon={Mail}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@example.com"
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none placeholder:text-zinc-300"
              />
            </Field>
          </div>

          <Field label="Employees" hintInline="estimate" icon={Users}>
            <input
              type="text"
              inputMode="numeric"
              value={form.employee_count_est}
              onChange={(e) => setForm({ ...form, employee_count_est: e.target.value.replace(/[^0-9]/g, '') })}
              className="w-32 px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] tabular-nums focus:border-amber-500 focus:outline-none"
            />
          </Field>

          <Field label="Stage">
            <SingleChips options={STAGES} value={form.stage}
              onChange={(v) => setForm({ ...form, stage: v })} dotMap={STAGE_DOT} />
          </Field>

          <Field label="Source">
            <SingleChips options={SOURCES} value={form.source}
              onChange={(v) => setForm({ ...form, source: v })} dotMap={SOURCE_DOT} allowClear />
          </Field>

          <Field label="Remark">
            <textarea rows={3} value={form.remark}
              onChange={(e) => setForm({ ...form, remark: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none resize-y" />
          </Field>

          {/* Company picker — pick existing or spin up a new one. Available in both
              create and edit modes; selected value is saved as lead.company_id. */}
          <div className="pt-3 border-t border-zinc-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] uppercase tracking-wider font-medium text-zinc-500 inline-flex items-center gap-1.5">
                <Building2 size={11} className="text-zinc-400" /> Company
              </span>
              <button
                type="button"
                onClick={() => setNewCoOpen(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11.5px] font-medium text-amber-700 hover:bg-amber-50"
                title="Open the full company form, primary contact pre-seeded from this lead"
              >
                <Plus size={11} strokeWidth={2.5} /> Create new
              </button>
            </div>

            <CompanyCombobox
              value={form.company}
              onChange={(c) => setForm({ ...form, company: c })}
              placeholder="Link to a company…"
            />

            {form.company_name_raw && !form.company && (
              <p className="mt-1.5 text-[11px] text-zinc-400">
                Raw text from intake: <span className="text-zinc-600">{form.company_name_raw}</span>
              </p>
            )}
          </div>

          {/* Follow-ups (edit mode only) */}
          {!isNew && (
            <div className="pt-4 border-t border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">
                  Follow-ups
                  {lead.follow_ups && (
                    <span className="text-zinc-400 normal-case tracking-normal font-normal ml-1.5">
                      ({lead.follow_ups.length})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => onAddFollowUp?.({
                    id: lead.id, name: form.name,
                    // Forward the linked company so the new follow-up auto-selects it.
                    company_id:   form.company?.id   ?? null,
                    company_name: form.company?.name || null,
                  })}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium text-amber-700 hover:bg-amber-50"
                >
                  <Plus size={12} strokeWidth={2.5} /> Log attempt
                </button>
              </div>
              <FollowUpItemList
                followUps={lead.follow_ups}
                onPick={(fu) => onEditFollowUp?.(fu, { id: lead.id, name: form.name })}
                emptyHint="No follow-up attempts logged yet."
              />
            </div>
          )}

          {err && (
            <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[12.5px]">{err}</div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-2 bg-zinc-50/60">
          {/* Delete — edit mode only, 2-step confirm */}
          {!isNew ? (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={deleting || saving}
              title="Delete this lead"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
            >
              {deleting
                ? <Loader2 size={13} className="animate-spin" />
                : <Trash2 size={13} strokeWidth={2.25} />}
              Delete
            </button>
          ) : <span />}

          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-zinc-700 hover:bg-zinc-100">
              Cancel
            </button>
            <button onClick={save} disabled={saving || deleting}
              className="px-3.5 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm shadow-amber-500/20">
              {saving && <Loader2 size={13} className="animate-spin" />}
              {isNew ? 'Create lead' : 'Save changes'}
            </button>
          </div>
        </footer>
      </aside>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete lead"
        message={<>Delete <strong>{form?.name || 'this lead'}</strong>? This also removes any follow-ups attached to the lead.</>}
        confirmLabel="Delete"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doDelete}
      />

      {/* Nested full CompanyDrawer for the "Create new company" flow. Sits
          above the lead drawer (z-40) so it doesn't get covered by the
          lead's backdrop. Primary contact is pre-seeded from the lead. */}
      {newCoOpen && companySeed && (
        <CompanyDrawer
          company={companySeed}
          zClass="z-40"
          onClose={() => setNewCoOpen(false)}
          onSaved={(isNewCo, created) => { if (isNewCo) onCompanyCreated(created) }}
        />
      )}
    </div>
  )
}

function Field({ label, hint, hintInline, icon: Icon, children }) {
  return (
    <div>
      <label className="block mb-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-zinc-500">
          {Icon && <Icon size={11} className="text-zinc-400" />}
          {label}
          {hintInline && (
            <span className="text-zinc-400 normal-case tracking-normal font-normal">
              ({hintInline})
            </span>
          )}
        </span>
        {hint && (
          <span className="block text-[11px] text-zinc-400 mt-0.5 normal-case tracking-normal font-normal">
            {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  )
}

function SingleChips({ options, value, onChange, dotMap, allowClear }) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => {
        const on = value === o
        return (
          <button
            key={o} type="button"
            onClick={() => onChange(allowClear && on ? '' : o)}
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] font-medium border transition-colors ' +
              (on
                ? 'bg-zinc-900 border-zinc-900 text-white'
                : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300')
            }
          >
            {dotMap && <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-white' : dotMap[o]}`} />}
            {o}
          </button>
        )
      })}
    </div>
  )
}

/* ──────────────────────────────── filter toolbar primitives ──────────────────────────────── */

function FilterButton({ icon: Icon, label, count, children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const active = count > 0
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium border transition-colors ' +
          (active
            ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
            : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300')
        }
      >
        <Icon size={13.5} strokeWidth={2.25} className={active ? 'text-amber-600' : 'text-zinc-500'} />
        {label}
        {active && (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10.5px] font-semibold tabular-nums">
            {count}
          </span>
        )}
        <ChevronDown size={12} className={'transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>

      {open && (
        <div className="absolute z-20 top-full mt-2 left-0 md:left-auto md:right-0 w-[calc(100vw-2rem)] sm:w-auto min-w-0 sm:min-w-[260px] max-w-[320px] bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.12)] p-1">
          {children}
        </div>
      )}
    </div>
  )
}

function ChipPanel({ options, value, onChange, dotMap }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  const allOn  = options.length > 0 && value.length === options.length
  const noneOn = value.length === 0
  return (
    <div>
      {/* Bulk actions */}
      <div className="flex items-center justify-between px-3 pt-2 pb-1.5 border-b border-zinc-100">
        <button
          onClick={() => onChange(options.slice())}
          disabled={allOn}
          className="text-[11.5px] font-medium text-amber-600 hover:text-amber-700 disabled:text-zinc-300 disabled:cursor-not-allowed"
        >
          Select all
        </button>
        <span className="text-[11px] text-zinc-400 tabular-nums">
          {value.length}/{options.length} selected
        </span>
        <button
          onClick={() => onChange([])}
          disabled={noneOn}
          className="text-[11.5px] font-medium text-zinc-500 hover:text-zinc-900 disabled:text-zinc-300 disabled:cursor-not-allowed"
        >
          Clear
        </button>
      </div>

      <div className="p-1">
        {options.map((o) => {
          const on = value.includes(o)
          return (
            <button
              key={o} onClick={() => toggle(o)}
              className={
                'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] text-left transition-colors ' +
                (on ? 'bg-amber-50 text-amber-900' : 'text-zinc-700 hover:bg-zinc-50')
              }
            >
              <span className={'shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ' +
                (on ? 'bg-amber-500 border-amber-500' : 'bg-white border-zinc-300')}>
                {on && (
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6.5L4.8 9 10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              {dotMap && <span className={`w-1.5 h-1.5 rounded-full ${dotMap[o]}`} />}
              <span className="flex-1 truncate">{o}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function SegmentedPanel({ options, value, onChange }) {
  return (
    <div className="p-1">
      {options.map((o) => {
        const on = value === o.v
        return (
          <button key={o.v} onClick={() => onChange(o.v)}
            className={
              'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] text-left transition-colors ' +
              (on ? 'bg-amber-50 text-amber-900 font-medium' : 'text-zinc-700 hover:bg-zinc-50')
            }>
            <span className={'shrink-0 w-3.5 h-3.5 rounded-full border flex items-center justify-center ' +
              (on ? 'bg-amber-500 border-amber-500' : 'bg-white border-zinc-300')}>
              {on && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            </span>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function DatePanel({ from, to, onChange }) {
  const presets = useMemo(() => {
    const fmt = (d) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    }
    const today = new Date()
    const todayStr = fmt(today)
    const ago = (n) => {
      const d = new Date()
      d.setDate(d.getDate() - n)
      return fmt(d)
    }
    return [
      { label: 'Today',        from: todayStr, to: todayStr },
      { label: 'Last 7 days',  from: ago(6),   to: todayStr },
      { label: 'Last 14 days', from: ago(13),  to: todayStr },
      { label: 'Last 1 month', from: ago(29),  to: todayStr },
    ]
  }, [])

  const activePreset = presets.find((p) => p.from === from && p.to === to)?.label

  return (
    <div className="p-3 min-w-[260px]">
      <div className="grid grid-cols-2 gap-1">
        {presets.map((p) => {
          const on = activePreset === p.label
          return (
            <button
              key={p.label}
              onClick={() => onChange(p.from, p.to)}
              className={
                'px-2.5 py-1.5 rounded-md text-[12px] font-medium text-left transition-colors ' +
                (on
                  ? 'bg-amber-100/80 text-amber-900 ring-1 ring-amber-200'
                  : 'text-zinc-700 hover:bg-zinc-50 ring-1 ring-transparent')
              }
            >
              {p.label}
            </button>
          )
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-zinc-100">
        <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 font-medium mb-1">From</label>
        <input type="date" value={from} onChange={(e) => onChange(e.target.value, to)}
          className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] font-medium focus:border-amber-500 focus:outline-none" />
        <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 font-medium mt-2.5 mb-1">To</label>
        <input type="date" value={to} onChange={(e) => onChange(from, e.target.value)}
          className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] font-medium focus:border-amber-500 focus:outline-none" />
      </div>

      {(from || to) && (
        <button onClick={() => onChange('', '')}
          className="mt-3 text-[11.5px] text-zinc-500 hover:text-zinc-900">Clear dates</button>
      )}
    </div>
  )
}

function ActiveChip({ icon: Icon, label, value, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 bg-amber-50 ring-1 ring-amber-200/70 rounded-md text-[11.5px] text-zinc-700">
      <Icon size={11} className="text-amber-600" />
      <span className="text-zinc-500">{label}:</span>
      <span className="font-medium text-zinc-900">{value}</span>
      <button onClick={onRemove}
        className="ml-0.5 w-4 h-4 rounded hover:bg-amber-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900"
        aria-label="Remove filter">
        <X size={11} />
      </button>
    </span>
  )
}

/* ──────────────────────────────── table primitives ──────────────────────────────── */

function SortHeader({ field, label, sort, dir, onClick, align = 'left', className = '' }) {
  const active = sort === field
  const Arrow = !active ? ChevronsUpDown : (dir === 'asc' ? ChevronUp : ChevronDown)
  return (
    <th className={`px-5 py-3 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>
      <button
        type="button"
        onClick={() => onClick(field)}
        className={
          'group inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-medium rounded transition-colors ' +
          (active ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900')
        }
      >
        <span>{label}</span>
        <Arrow size={12} strokeWidth={2.25}
          className={active ? 'text-amber-600' : 'text-zinc-400 group-hover:text-zinc-600'} />
      </button>
    </th>
  )
}

function StageBadge({ stage }) {
  const cls = STAGE_BADGE[stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STAGE_DOT[stage] || 'bg-zinc-400'}`} />
      {stage}
    </span>
  )
}

function Pager({ page, totalPages, onChange }) {
  const pages = useMemo(() => pageNumbers(page, totalPages), [page, totalPages])
  return (
    <div className="flex items-center gap-0.5">
      <PagerBtn disabled={page <= 1} onClick={() => onChange(page - 1)}><ChevronLeft size={13} /></PagerBtn>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="px-1 text-zinc-300">…</span>
        ) : (
          <PagerBtn key={p} active={p === page} onClick={() => onChange(p)}>{p}</PagerBtn>
        )
      )}
      <PagerBtn disabled={page >= totalPages} onClick={() => onChange(page + 1)}><ChevronRight size={13} /></PagerBtn>
    </div>
  )
}
function PagerBtn({ children, active, disabled, onClick }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={
        'min-w-[26px] h-7 px-1.5 rounded-md text-[12px] font-medium tabular-nums flex items-center justify-center transition-colors ' +
        (disabled ? 'text-zinc-300 cursor-not-allowed'
         : active  ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/20'
                   : 'text-zinc-600 hover:bg-zinc-100')
      }
    >{children}</button>
  )
}
function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const out = [1]
  const start = Math.max(2, current - 1)
  const end   = Math.min(total - 1, current + 1)
  if (start > 2) out.push('…')
  for (let p = start; p <= end; p++) out.push(p)
  if (end < total - 1) out.push('…')
  out.push(total)
  return out
}

/* ──────────────────────────────── grouping helpers ──────────────────────────────── */

function groupRowsByDate(rows) {
  const out = []
  for (const row of rows) {
    const ts  = row.created_at ? row.created_at * 1000 : 0
    const d   = new Date(ts)
    const key = ts ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : 'no-date'
    const label = ts
      ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
      : 'No date'
    const last = out[out.length - 1]
    if (last && last.key === key) last.rows.push(row)
    else out.push({ key, label, rows: [row], value: key })
  }
  return out
}
function groupRowsByStage(rows) {
  const out = []
  for (const row of rows) {
    const key = row.stage || 'no-stage'
    const last = out[out.length - 1]
    if (last && last.key === key) last.rows.push(row)
    else out.push({ key, label: key, rows: [row], value: key })
  }
  return out
}
function pad(n) { return String(n).padStart(2, '0') }
